import { createSlice } from '@reduxjs/toolkit';
import type { CompanyProfile } from '../../services/api/companyProfileService';
import {
  fetchCompanyProfile,
  saveCompanyProfile,
  saveCompanyProfileLabelling,
} from '../thunks/companyProfileThunks';

export interface CompanyProfileState {
  profile: CompanyProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  /** Separate saving flag for the Labelling tab (independent Save button). */
  isSavingLabelling: boolean;
  error: string | null;
  /** The supplier party id the cached profile belongs to (drives stale refetch). */
  loadedPartyId: number | null;
}

const initialState: CompanyProfileState = {
  profile: null,
  isLoading: false,
  isSaving: false,
  isSavingLabelling: false,
  error: null,
  loadedPartyId: null,
};

const companyProfileSlice = createSlice({
  name: 'companyProfile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.loadedPartyId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.loadedPartyId = action.payload.partyId;
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.profile = null;
        state.loadedPartyId = action.meta.arg.supplierPartyId;
      });

    builder
      .addCase(saveCompanyProfile.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveCompanyProfile.fulfilled, (state, action) => {
        state.isSaving = false;
        // Merge the saved values into the cached profile (no refetch needed).
        if (state.profile && state.profile.partyId === action.payload.request.supplierPartyId) {
          const r = action.payload.request;
          state.profile = {
            ...state.profile,
            companyName: r.companyName,
            contactPerson: r.contactPerson ?? null,
            tel: r.tel ?? null,
            fax: r.fax ?? null,
            email: r.email ?? null,
            addressLine1: r.addressLine1 ?? null,
            addressLine2: r.addressLine2 ?? null,
            addressLine3: r.addressLine3 ?? null,
            addressLine4: r.addressLine4 ?? null,
            city: r.city ?? null,
            provinceState: r.provinceState ?? null,
            postalCode: r.postalCode ?? null,
            country: r.country ?? null,
            countryCode: r.countryCode ?? null,
          };
        }
      })
      .addCase(saveCompanyProfile.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(saveCompanyProfileLabelling.pending, (state) => {
        state.isSavingLabelling = true;
        state.error = null;
      })
      .addCase(saveCompanyProfileLabelling.fulfilled, (state, action) => {
        state.isSavingLabelling = false;
        // Merge the saved labelling values into the cached profile (no refetch).
        if (state.profile && state.profile.partyId === action.payload.request.supplierPartyId) {
          const r = action.payload.request;
          state.profile = {
            ...state.profile,
            labelling: {
              deliveryType: r.deliveryType ?? null,
              courierName: r.courierName ?? null,
              accountNo: r.accountNo ?? null,
              contactNo: r.contactNo ?? null,
              addressLine1: r.addressLine1 ?? null,
              addressLine2: r.addressLine2 ?? null,
              addressLine3: r.addressLine3 ?? null,
              addressLine4: r.addressLine4 ?? null,
              city: r.city ?? null,
              provinceState: r.provinceState ?? null,
              postalCode: r.postalCode ?? null,
              country: r.country ?? null,
              countryCode: r.countryCode ?? null,
            },
          };
        }
      })
      .addCase(saveCompanyProfileLabelling.rejected, (state, action) => {
        state.isSavingLabelling = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearProfile } = companyProfileSlice.actions;
export default companyProfileSlice.reducer;
